import { IAppRouteUnitsContract } from "@features/app/route-units";
import { IPlatformContextContract } from "@features/launch-context/model";
import { IProductOnboardingContract } from "@features/onboarding/ui/productOnboarding";
import { StudentApi } from "@features/shared/api/course-data.api";
import {
    changeBrowserPageCloseClicked,
    changeBrowserPageCopyClicked,
    changeBrowserPagePresented,
    changeBrowserPageSkipClicked
} from "@features/shared/events";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IRouterContract } from "@features/shared/router";
import { IUserAuthContract } from "@features/student/auth/model";
import { createEffect, createEvent, createStore, sample } from "effector";

const skipButtonClicked = createEvent();
const copyButtonClicked = createEvent();
const closeButtonClicked = createEvent();

const $oneTimeToken = createStore<string | null>(null);
const $redirectUrl = createStore<string | null>(null);

const getOneTimeTokenFx = createEffect(async () => {
    const response = await StudentApi.getOneTimeToken();

    return response;
});
const trackChangeBrowserPagePresentedFx = createEffect(() => changeBrowserPagePresented());
const trackChangeBrowserPageSkipClickedFx = createEffect(() => changeBrowserPageSkipClicked());
const trackChangeBrowserPageCopyClickedFx = createEffect(() => changeBrowserPageCopyClicked());
const trackChangeBrowserPageCloseClickedFx = createEffect(() => changeBrowserPageCloseClicked());

sample({
    clock: getOneTimeTokenFx.doneData,
    filter: (data): data is { token: string } => data.token !== undefined,
    fn: (data) => data.token,
    target: $oneTimeToken
});

sample({
    clock: $oneTimeToken,
    fn: (token) => {
        const origin = typeof window !== "undefined" ? window.location.origin : "https://tixu.ai";
        const deviceId = typeof window !== "undefined" ? localStorage.getItem("user_uuid") : null;
        const deviceIdParam = deviceId ? `&dev_id=${deviceId}` : "";

        return `${origin}/home?token=${token}${deviceIdParam}`;
    },
    target: $redirectUrl
});

sample({
    clock: skipButtonClicked,
    target: trackChangeBrowserPageSkipClickedFx
});

sample({
    clock: copyButtonClicked,
    target: trackChangeBrowserPageCopyClickedFx
});

sample({
    clock: closeButtonClicked,
    target: trackChangeBrowserPageCloseClickedFx
});

const contract = new ModelContract(
    { $oneTimeToken, $redirectUrl },
    { skipButtonClicked, copyButtonClicked, closeButtonClicked },
    {},
    {}
);

export type IBrowserInstructionContract = typeof contract;

export class BrowserInstructionModule extends ModuleDescription<IBrowserInstructionContract> {
    constructor(
        public routerModule: IRouterContract,
        public routesModule: IAppRouteUnitsContract,
        public launchContextModule: IPlatformContextContract,
        public productOnboardingModule: IProductOnboardingContract,
        public authModule: IUserAuthContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            source: {
                isChangeBrowserRoute: this.routesModule.stores.changeBrowser.$isActive,
                student: this.authModule.stores.$student
            },
            filter: (s) => s.isChangeBrowserRoute && s.student !== null,
            target: [trackChangeBrowserPagePresentedFx, getOneTimeTokenFx]
        });

        sample({
            clock: [skipButtonClicked, closeButtonClicked],
            fn: () => "/home",
            target: [
                this.routerModule.events.routerPush,
                this.productOnboardingModule.events.finishUpsellAndStartProductOnboarding
            ]
        });

        sample({
            source: {
                isChangeBrowserRoute: this.routesModule.stores.changeBrowser.$isActive,
                isMetaBrowser: this.launchContextModule.stores.$isMetaBrowser,
                token: $oneTimeToken
            },
            filter: (s) => s.isChangeBrowserRoute && !s.isMetaBrowser && s.token !== null,
            fn: () => "/home",
            target: this.routerModule.events.routerReplace
        });
    }
}
